import { useState, useEffect, useCallback, useRef } from "react";
import { useRiskEngine, ASSET_REGISTRY } from "../hooks/useRiskEngine";
import { useMarketData } from "../hooks/useMarketData";

// ============================================================
// CONSTANTS
// ============================================================
const ACCOUNT_SIZES = [10000, 25000, 50000, 75000, 100000, 150000, 200000, 300000, 500000];
const RISK_COLORS = { Safe: "#00ff9d", Caution: "#f59e0b", "High Risk": "#ef4444", Liquidated: "#7f1d1d" };
const fmt = (n, dec = 2) => Number(n).toLocaleString("en-US", { minimumFractionDigits: dec, maximumFractionDigits: dec });
const fmtDollar = (n) => (n < 0 ? "-$" : "$") + fmt(Math.abs(n));

// ============================================================
// HOOKS
// ============================================================
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" ? window.innerWidth < 768 : false);
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return isMobile;
}

// ============================================================
// FIXED INPUT COMPONENT
// The key fix: value is stored as a string ref internally.
// Parent state only updates on blur, preventing focus loss on every keystroke.
// ============================================================
function TerminalInput({ label, value, onChange, allowNegative = false, suffix, disabled = false }) {
  const inputRef = useRef(null);
  const [display, setDisplay] = useState(String(value));
  const isFocused = useRef(false);

  // Only sync from parent when not focused
  useEffect(() => {
    if (!isFocused.current) {
      setDisplay(String(value));
    }
  }, [value]);

  const handleChange = (e) => {
    const v = e.target.value;
    // Allow typing: digits, minus at start, single decimal
    if (v === "" || v === "-" || /^-?\d*\.?\d*$/.test(v)) {
      setDisplay(v);
    }
  };

  const handleFocus = () => { isFocused.current = true; };

  const handleBlur = () => {
    isFocused.current = false;
    const parsed = parseFloat(display);
    if (isNaN(parsed)) {
      setDisplay(String(value));
    } else {
      const final = allowNegative ? parsed : Math.max(parsed, 0);
      setDisplay(String(final));
      onChange(final);
    }
  };

  const isNeg = parseFloat(display) < 0;

  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 9, color: disabled ? "rgba(255,255,255,0.15)" : "rgba(255,255,255,0.3)", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 5, fontFamily: "monospace" }}>{label}</div>
      <div style={{ position: "relative" }}>
        <input
          ref={inputRef}
          type="text"
          inputMode="decimal"
          value={display}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={disabled}
          style={{
            width: "100%", background: disabled ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.4)",
            border: `1px solid ${isNeg ? "rgba(239,68,68,0.4)" : disabled ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.1)"}`,
            borderRadius: 3, padding: "9px 12px",
            color: disabled ? "rgba(255,255,255,0.2)" : isNeg ? "#ef4444" : "#00ff9d",
            fontSize: 13, fontFamily: "monospace", outline: "none", boxSizing: "border-box",
            cursor: disabled ? "not-allowed" : "text",
          }}
        />
        {suffix && <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", fontSize: 10, color: "rgba(255,255,255,0.2)", fontFamily: "monospace" }}>{suffix}</span>}
      </div>
    </div>
  );
}

// ============================================================
// SLIDER COMPONENT
// ============================================================
function TerminalSlider({ label, value, onChange, min, max, step, format, sublabel }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
        <div style={{ fontSize: 9, color: "rgba(255,255,255,0.3)", letterSpacing: "0.12em", textTransform: "uppercase", fontFamily: "monospace" }}>{label}</div>
        <div style={{ fontSize: 11, color: "#00ff9d", fontFamily: "monospace" }}>{format ? format(value) : value}</div>
      </div>
      <input type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        style={{ width: "100%", height: 3, background: "rgba(255,255,255,0.08)", borderRadius: 0, cursor: "pointer", appearance: "none", WebkitAppearance: "none", outline: "none" }}
      />
      {sublabel && <div style={{ fontSize: 9, color: "rgba(255,255,255,0.2)", fontFamily: "monospace", marginTop: 4 }}>{sublabel}</div>}
    </div>
  );
}

// ============================================================
// SELECT COMPONENT
// ============================================================
function SelectField({ label, value, onChange, options, disabled = false }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 9, color: disabled ? "rgba(255,255,255,0.15)" : "rgba(255,255,255,0.3)", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 5, fontFamily: "monospace" }}>{label}</div>
      <div style={{ position: "relative" }}>
        <select value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled}
          style={{ width: "100%", background: "rgba(0,0,0,0.4)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 3, padding: "9px 12px", color: disabled ? "rgba(255,255,255,0.2)" : "#00ff9d", fontSize: 12, fontFamily: "monospace", outline: "none", cursor: disabled ? "not-allowed" : "pointer", appearance: "none", WebkitAppearance: "none" }}>
          {options.map(([val, lbl]) => <option key={val} value={val}>{lbl}</option>)}
        </select>
        <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", color: "rgba(255,255,255,0.3)", pointerEvents: "none", fontSize: 10 }}>▾</span>
      </div>
    </div>
  );
}

// ============================================================
// TOGGLE COMPONENT
// ============================================================
function Toggle({ label, value, onChange, sublabel }) {
  return (
    <div style={{ background: value ? "rgba(239,68,68,0.08)" : "rgba(255,255,255,0.025)", border: `1px solid ${value ? "rgba(239,68,68,0.3)" : "rgba(255,255,255,0.07)"}`, borderRadius: 4, padding: "12px 14px", marginBottom: 10, cursor: "pointer" }}
      onClick={() => onChange(!value)}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ fontSize: 11, color: value ? "#ef4444" : "rgba(255,255,255,0.6)", fontFamily: "monospace", fontWeight: 700 }}>{label}</div>
          {sublabel && <div style={{ fontSize: 9, color: "rgba(255,255,255,0.25)", fontFamily: "monospace", marginTop: 2 }}>{sublabel}</div>}
        </div>
        <div style={{ width: 34, height: 18, background: value ? "#ef4444" : "rgba(255,255,255,0.1)", borderRadius: 10, position: "relative", transition: "all 0.2s", flexShrink: 0 }}>
          <div style={{ position: "absolute", top: 2, left: value ? 18 : 2, width: 14, height: 14, background: "#fff", borderRadius: "50%", transition: "all 0.2s" }} />
        </div>
      </div>
    </div>
  );
}

// ============================================================
// RISK GAUGE
// ============================================================
function RiskGauge({ Mv, riskLevel, size = 200 }) {
  const clampedMv = Math.min(Math.max(Mv, 0), 2.0);
  const pct = clampedMv / 2.0;
  const angle = -180 + pct * 180;
  const color = RISK_COLORS[riskLevel] || "#00ff9d";
  const cx = size / 2, cy = size * 0.55, r = size * 0.4;
  const polar = (cx, cy, r, deg) => { const rad = (deg * Math.PI) / 180; return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) }; };
  const arc = (cx, cy, r, s, e) => { const sp = polar(cx, cy, r, s); const ep = polar(cx, cy, r, e); return `M ${sp.x} ${sp.y} A ${r} ${r} 0 ${e - s > 180 ? 1 : 0} 1 ${ep.x} ${ep.y}`; };
  const needle = polar(cx, cy, r - size * 0.06, angle);
  return (
    <svg width={size} height={size * 0.65} style={{ overflow: "visible" }}>
      <path d={arc(cx, cy, r, -180, 0)} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={size * 0.07} strokeLinecap="round" />
      <path d={arc(cx, cy, r, -180, -120)} fill="none" stroke="rgba(239,68,68,0.3)" strokeWidth={size * 0.07} strokeLinecap="butt" />
      <path d={arc(cx, cy, r, -120, -60)} fill="none" stroke="rgba(245,158,11,0.3)" strokeWidth={size * 0.07} strokeLinecap="butt" />
      <path d={arc(cx, cy, r, -60, 0)} fill="none" stroke="rgba(0,255,157,0.3)" strokeWidth={size * 0.07} strokeLinecap="butt" />
      <path d={arc(cx, cy, r, -180, angle)} fill="none" stroke={color} strokeWidth={size * 0.07} strokeLinecap="round"
        style={{ filter: `drop-shadow(0 0 6px ${color})`, transition: "all 0.5s ease" }} />
      <line x1={cx} y1={cy} x2={needle.x} y2={needle.y} stroke={color} strokeWidth="2" style={{ transition: "all 0.5s ease" }} />
      <circle cx={cx} cy={cy} r="4" fill={color} />
      <text x={cx} y={cy + size * 0.14} fill={color} fontSize={size * 0.1} fontWeight="bold" fontFamily="monospace" textAnchor="middle">{fmt(Mv, 3)}</text>
      <text x={cx} y={cy + size * 0.23} fill="rgba(255,255,255,0.2)" fontSize={size * 0.043} fontFamily="monospace" textAnchor="middle">VOLATILITY MULTIPLIER</text>
    </svg>
  );
}

// ============================================================
// MAIN TERMINAL
// ============================================================
export default function DareTerminal() {
  const isMobile = useIsMobile();

  // ── Volatility inputs (auto-populated from market data)
  const [currentVix, setCurrentVix] = useState(18.5);
  const [vix20MA, setVix20MA] = useState(19.5);
  const [atr5, setAtr5] = useState(300);
  const [selectedAsset, setSelectedAsset] = useState("NQ");

  // ── Account settings
  const [accountSize, setAccountSize] = useState("50000");
  const [drawdownType, setDrawdownType] = useState("intraday");
  const [maxLossLimit, setMaxLossLimit] = useState(2500);
  const [hasDLL, setHasDLL] = useState("yes");
  const [dllAmount, setDllAmount] = useState(1500);
  const [hasConsistency, setHasConsistency] = useState("yes");
  const [profitTarget, setProfitTarget] = useState(3000);
  const [currentDayProfit, setCurrentDayProfit] = useState(0);

  // ── DARE parameters
  const [riskPerTradeRatio, setRiskPerTradeRatio] = useState(0.30);
  const [stopFraction, setStopFraction] = useState(0.25);
  const [isMarketOpenWindow, setIsMarketOpenWindow] = useState(false);

  // ── Derived account values
  const balance = parseFloat(accountSize) || 50000;
  const drawdownFloor = balance - maxLossLimit;
  const dailyLossLimit = hasDLL === "yes" ? dllAmount : maxLossLimit;

  // ── Live market data
  const market = useMarketData(selectedAsset);
  useEffect(() => {
    if (market.atr5 && !market.loading) setAtr5(market.atr5);
    if (market.vix && !market.loading) setCurrentVix(market.vix);
    if (market.vix20MA && !market.loading) setVix20MA(market.vix20MA);
  }, [market.atr5, market.vix, market.vix20MA, market.loading]);

  // ── DARE Engine
  const engine = useRiskEngine({
    balance,
    drawdownFloor,
    currentVix,
    vix20MA,
    atr5,
    selectedAsset,
    dailyLossLimit,
    riskPerTradeRatio,
    stopFraction,
    isMarketOpenWindow,
    profitTarget,
    currentDayProfit,
  });

  const riskColor = RISK_COLORS[engine.riskLevel] || "#00ff9d";
  const isLiquidated = engine.status === "Liquidated";

  // ── Tilt detection
  const sessionLoss = Math.min(currentDayProfit, 0);
  const isTilting = engine.Dd > 0 && (Math.abs(sessionLoss) / engine.Dd) > 0.5;
  const nearDLL = hasDLL === "yes" && dllAmount > 0 && Math.abs(sessionLoss) >= dllAmount * 0.8;

  // ── Mobile tabs
  const [activeTab, setActiveTab] = useState("position");
  const TABS = [["position", "Position"], ["account", "Account"], ["inputs", "Inputs"]];

  // ============================================================
  // ACCOUNT PANEL
  // ============================================================
  const AccountPanel = useCallback(() => (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ fontSize: 9, color: "rgba(255,255,255,0.2)", letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: 2 }}>Live Stats</div>

      <div style={{ background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 4, padding: "12px 14px" }}>
        <div style={{ fontSize: 9, color: "rgba(255,255,255,0.3)", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 4, fontFamily: "monospace" }}>Current Balance</div>
        <div style={{ fontSize: 20, fontWeight: 700, color: "rgba(255,255,255,0.9)", fontFamily: "monospace" }}>{fmtDollar(balance)}</div>
      </div>

      <div style={{ background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 4, padding: "12px 14px" }}>
        <div style={{ fontSize: 9, color: "rgba(255,255,255,0.3)", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 4, fontFamily: "monospace" }}>Drawdown Floor</div>
        <div style={{ fontSize: 20, fontWeight: 700, color: "#f59e0b", fontFamily: "monospace" }}>{fmtDollar(drawdownFloor)}</div>
      </div>

      {/* Distance to Death */}
      <div style={{ background: isLiquidated ? "rgba(127,29,29,0.2)" : engine.Dd < 1000 ? "rgba(239,68,68,0.1)" : "rgba(0,255,157,0.05)", border: `1px solid ${isLiquidated ? "#7f1d1d" : engine.Dd < 1000 ? "rgba(239,68,68,0.3)" : "rgba(0,255,157,0.15)"}`, borderRadius: 4, padding: "14px" }}>
        <div style={{ fontSize: 9, color: "rgba(255,255,255,0.3)", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6, fontFamily: "monospace" }}>Distance to Death</div>
        <div style={{ fontSize: 36, fontWeight: 900, color: isLiquidated ? "#ef4444" : engine.Dd < 1000 ? "#ef4444" : "#00ff9d", fontFamily: "monospace", lineHeight: 1 }}>
          {isLiquidated ? "DEAD" : fmtDollar(engine.Dd)}
        </div>
        <div style={{ fontSize: 10, color: "rgba(255,255,255,0.25)", marginTop: 5, fontFamily: "monospace" }}>
          {isLiquidated ? "Account liquidated" : `${((engine.Dd / balance) * 100).toFixed(2)}% of balance`}
        </div>
        {!isLiquidated && (
          <div style={{ marginTop: 10, background: "rgba(255,255,255,0.05)", borderRadius: 2, height: 4 }}>
            <div style={{ height: "100%", width: `${Math.min((engine.Dd / maxLossLimit) * 100, 100)}%`, background: engine.Dd < 1000 ? "#ef4444" : "#00ff9d", transition: "width 0.4s ease" }} />
          </div>
        )}
      </div>

      {/* Today P&L */}
      <div style={{ background: "rgba(255,255,255,0.025)", border: `1px solid ${currentDayProfit < 0 ? "rgba(239,68,68,0.25)" : "rgba(255,255,255,0.07)"}`, borderRadius: 4, padding: "12px 14px" }}>
        <div style={{ fontSize: 9, color: "rgba(255,255,255,0.3)", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 4, fontFamily: "monospace" }}>Today's P&L</div>
        <div style={{ fontSize: 22, fontWeight: 700, color: currentDayProfit < 0 ? "#ef4444" : currentDayProfit > 0 ? "#00ff9d" : "rgba(255,255,255,0.5)", fontFamily: "monospace" }}>
          {fmtDollar(currentDayProfit)}
        </div>
      </div>

      {/* DLL Warning */}
      {hasDLL === "yes" && nearDLL && (
        <div style={{ padding: "10px 12px", background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 4, fontSize: 10, color: "#ef4444", fontFamily: "monospace" }}>
          ⚠ 80% of Daily Loss Limit reached — {fmtDollar(dllAmount - Math.abs(sessionLoss))} remaining
        </div>
      )}

      {/* Consistency */}
      {hasConsistency === "yes" && (
        <div style={{ background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 4, padding: "14px" }}>
          <div style={{ fontSize: 9, color: "rgba(255,255,255,0.3)", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 8, fontFamily: "monospace" }}>Consistency Check</div>
          <div style={{ background: "rgba(255,255,255,0.06)", borderRadius: 2, height: 6, overflow: "hidden", marginBottom: 6 }}>
            <div style={{ height: "100%", width: `${Math.min(engine.consistency?.percentUsed || 0, 100)}%`, background: (engine.consistency?.percentUsed || 0) > 90 ? "#ef4444" : "#00ff9d", transition: "width 0.4s ease" }} />
          </div>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span style={{ fontSize: 10, color: "rgba(255,255,255,0.25)", fontFamily: "monospace" }}>Today: {fmtDollar(currentDayProfit)}</span>
            <span style={{ fontSize: 10, color: "rgba(255,255,255,0.25)", fontFamily: "monospace" }}>Cap: {fmtDollar(engine.consistency?.consistencyCap || 0)}</span>
          </div>
          {engine.consistency?.isBreached && (
            <div style={{ marginTop: 8, padding: "6px 10px", background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.2)", borderRadius: 3, fontSize: 10, color: "#ef4444", fontFamily: "monospace" }}>⚠ CONSISTENCY CAP BREACHED</div>
          )}
        </div>
      )}

      <div style={{ height: 1, background: "rgba(255,255,255,0.07)", margin: "4px 0" }} />
      <div style={{ fontSize: 9, color: "rgba(255,255,255,0.2)", letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: 2 }}>Account Settings</div>

      <SelectField label="Account Size" value={accountSize} onChange={setAccountSize}
        options={ACCOUNT_SIZES.map((s) => [String(s), `$${s.toLocaleString()}`])} />

      <SelectField label="Drawdown Type" value={drawdownType} onChange={setDrawdownType}
        options={[["intraday", "Intraday (Real-time trailing)"], ["eod", "End of Day (EOD)"]]} />

      <TerminalInput label="Max Loss Limit ($)" value={maxLossLimit} onChange={setMaxLossLimit} suffix="USD" />

      <SelectField label="Daily Loss Limit (DLL)" value={hasDLL} onChange={setHasDLL}
        options={[["yes", "Yes — Firm has a DLL"], ["no", "No DLL"]]} />
      {hasDLL === "yes" && (
        <TerminalInput label="DLL Amount ($)" value={dllAmount} onChange={setDllAmount} suffix="USD" />
      )}

      <SelectField label="Consistency Rule" value={hasConsistency} onChange={setHasConsistency}
        options={[["yes", "Yes — Firm has consistency rule"], ["no", "No consistency rule"]]} />

      <TerminalInput label="Profit Target ($)" value={profitTarget} onChange={setProfitTarget} suffix="USD" />
      <TerminalInput label="Today's P&L" value={currentDayProfit} onChange={setCurrentDayProfit} allowNegative={true} suffix="USD" />

      <div style={{ padding: "10px 12px", background: "rgba(0,255,157,0.03)", border: "1px solid rgba(0,255,157,0.08)", borderRadius: 3, marginTop: 4 }}>
        <div style={{ fontSize: 9, color: "rgba(0,255,157,0.4)", fontFamily: "monospace", lineHeight: 1.8 }}>
          🔮 Coming soon: Select your prop firm and account size to auto-populate all rules.
        </div>
      </div>
    </div>
  ), [balance, drawdownFloor, engine, currentDayProfit, hasDLL, dllAmount, nearDLL, sessionLoss, hasConsistency, accountSize, drawdownType, maxLossLimit, profitTarget, isLiquidated]);

  // ============================================================
  // POSITION PANEL
  // ============================================================
  const PositionPanel = useCallback(() => (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

      {/* TILT WARNING */}
      {(isTilting || nearDLL) && (
        <div style={{ background: "rgba(239,68,68,0.12)", border: "2px solid #ef4444", borderRadius: 4, padding: "16px 18px", animation: "tiltPulse 2s ease-in-out infinite" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <span style={{ fontSize: 18 }}>⚠️</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#ef4444", fontFamily: "monospace", letterSpacing: "0.1em" }}>TILT WARNING — STOP TRADING NOW</span>
          </div>
          <div style={{ fontSize: 11, color: "rgba(239,68,68,0.8)", fontFamily: "monospace", lineHeight: 1.9 }}>
            {isTilting && "You have used over 50% of your Distance to Death this session. "}
            {nearDLL && "You are within 20% of your Daily Loss Limit. "}
            Step away. Close the platform. Come back tomorrow.
            <br /><br />
            <span style={{ color: "#ef4444", fontWeight: 700 }}>Rule: When in doubt, sit out.</span>
          </div>
        </div>
      )}

      {/* Gauge + Recommendation */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 14 }}>
        <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 4, padding: 18, display: "flex", flexDirection: "column", alignItems: "center" }}>
          <div style={{ fontSize: 9, color: "rgba(255,255,255,0.2)", letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: 12 }}>Volatility Gauge</div>
          <RiskGauge Mv={engine.Mv || 0} riskLevel={engine.riskLevel || "High Risk"} size={isMobile ? 160 : 190} />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, width: "100%", marginTop: 10 }}>
            {[["VIX / 20MA", fmt(engine.Mv || 0, 3)], ["ATR Ratio", fmt(engine.atrRatio || 1, 3)]].map(([lbl, val]) => (
              <div key={lbl} style={{ background: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 3, padding: "8px", textAlign: "center" }}>
                <div style={{ fontSize: 9, color: "rgba(255,255,255,0.2)", marginBottom: 3 }}>{lbl}</div>
                <div style={{ fontSize: 14, color: "#f59e0b", fontWeight: 700, fontFamily: "monospace" }}>{val}</div>
              </div>
            ))}
          </div>
          {isMarketOpenWindow && <div style={{ marginTop: 10, width: "100%", padding: "6px", background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.2)", borderRadius: 3, textAlign: "center", fontSize: 9, color: "#ef4444", fontFamily: "monospace" }}>×0.67 SURVIVE THE OPEN ACTIVE</div>}
        </div>

        <div style={{ background: `rgba(0,255,157,0.04)`, border: `1px solid ${riskColor}30`, borderRadius: 4, padding: 18, textAlign: "center", position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", inset: 0, background: `radial-gradient(ellipse at 50% 0%, ${riskColor}08, transparent 70%)`, pointerEvents: "none" }} />
          <div style={{ fontSize: 9, color: "rgba(255,255,255,0.3)", letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: 8 }}>DARE Recommendation</div>
          <div style={{ fontFamily: "monospace", fontWeight: 900, fontSize: isMobile ? 60 : 76, lineHeight: 1, color: riskColor, textShadow: `0 0 30px ${riskColor}`, marginBottom: 6 }}>
            {engine.position?.count || 0}
          </div>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(0,0,0,0.3)", border: `1px solid ${riskColor}40`, borderRadius: 20, padding: "5px 14px", marginBottom: 10 }}>
            <div style={{ width: 5, height: 5, borderRadius: "50%", background: riskColor }} />
            <span style={{ fontSize: 11, color: riskColor, fontFamily: "monospace", fontWeight: 700, letterSpacing: "0.1em" }}>
              {engine.position?.count === 0 ? "NO TRADE" : `${engine.position?.type} CONTRACT${engine.position?.count !== 1 ? "S" : ""}`}
            </span>
          </div>
          <div style={{ marginBottom: 10 }}>
            <span style={{ display: "inline-block", background: `${riskColor}15`, border: `1px solid ${riskColor}40`, borderRadius: 3, padding: "3px 12px", fontSize: 10, color: riskColor, fontFamily: "monospace", letterSpacing: "0.12em" }}>{engine.riskLevel?.toUpperCase()}</span>
          </div>
          {engine.position?.count > 0 && (
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", fontFamily: "monospace", marginBottom: 4 }}>
              Est. {fmtDollar(engine.dollarRiskAtStop || 0)} risk at stop
            </div>
          )}
          <div style={{ fontSize: 10, color: "rgba(255,255,255,0.2)", fontFamily: "monospace" }}>
            ${fmt(engine.dollarStopPerContract || 0, 0)} per contract · {fmt(stopFraction * 100, 0)}% ATR stop
          </div>
          {isMarketOpenWindow && <div style={{ marginTop: 10, padding: "5px", background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.2)", borderRadius: 3, fontSize: 9, color: "#ef4444", fontFamily: "monospace" }}>🔴 SIZE REDUCED 33%</div>}
        </div>
      </div>

      {/* Engine Breakdown */}
      <div style={{ background: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 4, padding: 16 }}>
        <div style={{ fontSize: 9, color: "rgba(255,255,255,0.2)", letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: 12 }}>Engine Breakdown</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
          {[
            ["Distance to Death", fmtDollar(engine.Dd || 0)],
            ["Volatility Multiplier", fmt(engine.Mv || 0, 3)],
            ["$ Risk Per Trade", fmtDollar(engine.dollarRiskPerTrade || 0)],
            ["$ Stop Per Contract", fmtDollar(engine.dollarStopPerContract || 0)],
          ].map(([key, val]) => (
            <div key={key} style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 3, padding: "8px 10px" }}>
              <div style={{ fontSize: 8, color: "rgba(255,255,255,0.3)", marginBottom: 4, fontFamily: "monospace", letterSpacing: "0.06em", textTransform: "uppercase" }}>{key}</div>
              <div style={{ fontSize: isMobile ? 11 : 14, fontWeight: 700, color: "rgba(255,255,255,0.8)", fontFamily: "monospace" }}>{val}</div>
            </div>
          ))}
        </div>
      </div>

      {/* R:R Targets */}
      <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 4, padding: 16 }}>
        <div style={{ fontSize: 9, color: "rgba(255,255,255,0.2)", letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: 10 }}>
          R:R Targets · {engine.position?.count} {engine.position?.type}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 8 }}>
          {[1, 1.5, 2, 3].map((r) => (
            <div key={r} style={{ background: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 3, padding: "10px 8px", textAlign: "center" }}>
              <div style={{ fontSize: 9, color: "rgba(255,255,255,0.2)", marginBottom: 4 }}>{r}:1</div>
              <div style={{ fontSize: isMobile ? 12 : 14, fontWeight: 700, color: "#00ff9d", fontFamily: "monospace" }}>{fmtDollar((engine.dollarRiskAtStop || 0) * r)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  ), [engine, isMobile, riskColor, isMarketOpenWindow, isTilting, nearDLL, stopFraction]);

  // ============================================================
  // INPUTS PANEL
  // ============================================================
  const InputsPanel = useCallback(() => (
    <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>

      {/* Asset selector */}
      <div style={{ fontSize: 9, color: "rgba(255,255,255,0.2)", letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: 10 }}>Instrument</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6, marginBottom: 14 }}>
        {Object.entries(ASSET_REGISTRY).map(([sym, data]) => (
          <button key={sym} onClick={() => setSelectedAsset(sym)} style={{ background: selectedAsset === sym ? `${riskColor}15` : "rgba(255,255,255,0.02)", border: `1px solid ${selectedAsset === sym ? `${riskColor}40` : "rgba(255,255,255,0.07)"}`, borderRadius: 3, padding: "8px", cursor: "pointer", textAlign: "left" }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: selectedAsset === sym ? riskColor : "rgba(255,255,255,0.6)", fontFamily: "monospace" }}>{sym}</div>
            <div style={{ fontSize: 8, color: "rgba(255,255,255,0.2)", marginTop: 1 }}>{data.microSymbol}</div>
          </button>
        ))}
      </div>

      <div style={{ height: 1, background: "rgba(255,255,255,0.05)", margin: "4px 0 12px" }} />

      {/* Live data status */}
      <div style={{ fontSize: 9, color: "rgba(255,255,255,0.2)", letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: 8 }}>
        Volatility Data
        <button onClick={() => market.refresh()} style={{ marginLeft: 10, background: "rgba(0,255,157,0.08)", border: "1px solid rgba(0,255,157,0.25)", borderRadius: 3, padding: "2px 8px", fontSize: 8, color: "#00ff9d", cursor: "pointer", fontFamily: "monospace" }}>
          {market.loading ? "..." : "↻ REFRESH"}
        </button>
      </div>
      {market.error && <div style={{ padding: "7px 10px", background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.2)", borderRadius: 3, marginBottom: 10, fontSize: 9, color: "#ef4444", fontFamily: "monospace" }}>Live fetch failed — edit values manually below</div>}
      {market.atr5 && !market.error && <div style={{ padding: "7px 10px", background: "rgba(0,255,157,0.04)", border: "1px solid rgba(0,255,157,0.1)", borderRadius: 3, marginBottom: 10, fontSize: 9, color: "rgba(0,255,157,0.6)", fontFamily: "monospace" }}>Auto-populated · {market.asOf} · ~15min delayed</div>}

      <TerminalInput label="Current VIX" value={currentVix} onChange={setCurrentVix} suffix="pts" />
      <TerminalInput label="VIX 20-Day MA" value={vix20MA} onChange={setVix20MA} suffix="pts" />
      <TerminalInput label={`ATR 5-Day · ${selectedAsset}`} value={atr5} onChange={setAtr5} suffix="pts" />

      <div style={{ height: 1, background: "rgba(255,255,255,0.05)", margin: "4px 0 12px" }} />
      <div style={{ fontSize: 9, color: "rgba(255,255,255,0.2)", letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: 10 }}>DARE Parameters</div>

      {/* Risk per trade slider */}
      <TerminalSlider
        label="Risk Per Trade"
        value={riskPerTradeRatio}
        onChange={setRiskPerTradeRatio}
        min={0.05} max={0.80} step={0.05}
        format={(v) => `${Math.round(v * 100)}% of DLL = ${fmtDollar(dailyLossLimit * v)}`}
        sublabel="How much of your DLL to risk on one trade"
      />

      {/* Stop fraction slider */}
      <TerminalSlider
        label="Stop Width"
        value={stopFraction}
        onChange={setStopFraction}
        min={0.10} max={1.0} step={0.05}
        format={(v) => `${Math.round(v * 100)}% of ATR = ${fmtDollar((atr5 * v / (engine.asset?.tickSize || 0.25)) * (engine.asset?.tickValueMicro || 0.5))} / micro`}
        sublabel="Wider stop = fewer contracts, more room"
      />

      <Toggle label="Survive the Open" value={isMarketOpenWindow} onChange={setIsMarketOpenWindow} sublabel="Reduces size 33% during first 30 min" />

      <div style={{ height: 1, background: "rgba(255,255,255,0.05)", margin: "4px 0 12px" }} />

      {/* Asset registry */}
      <div style={{ background: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 3, padding: "12px 14px" }}>
        <div style={{ fontSize: 9, color: "rgba(255,255,255,0.2)", letterSpacing: "0.12em", marginBottom: 8, textTransform: "uppercase" }}>{selectedAsset} · Registry</div>
        {[
          ["Tick Size", engine.asset?.tickSize],
          ["Mini Tick $", fmtDollar(engine.asset?.tickValueMini || 0)],
          ["Micro Tick $", fmtDollar(engine.asset?.tickValueMicro || 0)],
          ["ADR Target", `${engine.asset?.adrTarget || 0} pts`],
          ["Micro Symbol", engine.asset?.microSymbol],
        ].map(([k, v]) => (
          <div key={k} style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
            <span style={{ fontSize: 9, color: "rgba(255,255,255,0.2)", fontFamily: "monospace" }}>{k}</span>
            <span style={{ fontSize: 9, color: "rgba(255,255,255,0.5)", fontFamily: "monospace" }}>{v}</span>
          </div>
        ))}
      </div>
    </div>
  ), [selectedAsset, riskColor, market, currentVix, vix20MA, atr5, riskPerTradeRatio, stopFraction, isMarketOpenWindow, dailyLossLimit, engine]);

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div style={{ minHeight: "100vh", background: "#0a0f14", color: "#e2e8f0", fontFamily: "monospace" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;700;800&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        select option { background: #0a0f14; color: #00ff9d; }
        input[type=range] { accent-color: #00ff9d; }
        @keyframes blink { 0%,100%{opacity:1} 50%{opacity:0.3} }
        @keyframes pulse-ring { 0%{box-shadow:0 0 0 0 rgba(0,255,157,0.3)} 70%{box-shadow:0 0 0 6px rgba(0,255,157,0)} 100%{box-shadow:0 0 0 0 rgba(0,255,157,0)} }
        @keyframes tiltPulse { 0%,100%{border-color:#ef4444;box-shadow:0 0 0 0 rgba(239,68,68,0)} 50%{border-color:#ff6666;box-shadow:0 0 24px rgba(239,68,68,0.35)} }
        ::-webkit-scrollbar { width: 3px; } ::-webkit-scrollbar-track { background: #0a0f14; } ::-webkit-scrollbar-thumb { background: #1e293b; }
      `}</style>

      {/* TOP BAR */}
      <div style={{ background: "#060b10", borderBottom: "1px solid rgba(255,255,255,0.06)", padding: `0 ${isMobile ? "16px" : "24px"}`, height: isMobile ? 48 : 52, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 8 : 12 }}>
          <div style={{ width: 7, height: 7, background: "#00ff9d", animation: "pulse-ring 2s infinite" }} />
          <span style={{ fontSize: isMobile ? 12 : 14, fontWeight: 800, letterSpacing: "0.08em", color: "#fff" }}>DARE</span>
          {!isMobile && <span style={{ fontSize: 10, color: "rgba(255,255,255,0.2)", letterSpacing: "0.15em" }}>DYNAMIC ADAPTIVE RISK ENGINE</span>}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {market.loading && <span style={{ fontSize: 9, color: "#f59e0b", animation: "blink 1s infinite", fontFamily: "monospace" }}>FETCHING...</span>}
          {!market.loading && market.atr5 && !market.error && <span style={{ fontSize: 9, color: "rgba(0,255,157,0.5)", fontFamily: "monospace" }}>LIVE · {market.asOf}</span>}
          {!market.loading && market.error && <span style={{ fontSize: 9, color: "#ef4444", fontFamily: "monospace" }}>MANUAL MODE</span>}
          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <div style={{ width: 5, height: 5, borderRadius: "50%", background: isLiquidated ? "#ef4444" : riskColor, animation: "blink 2s infinite" }} />
            <span style={{ fontSize: 9, color: isLiquidated ? "#ef4444" : riskColor, letterSpacing: "0.1em" }}>{engine.riskLevel?.toUpperCase()}</span>
          </div>
          <a href="/" style={{ fontSize: 9, color: "rgba(255,255,255,0.2)", textDecoration: "none" }}>← HOME</a>
        </div>
      </div>

      {/* MOBILE TABS */}
      {isMobile && (
        <div style={{ display: "flex", borderBottom: "1px solid rgba(255,255,255,0.06)", background: "#060b10" }}>
          {TABS.map(([id, label]) => (
            <button key={id} onClick={() => setActiveTab(id)} style={{ flex: 1, background: activeTab === id ? "rgba(0,255,157,0.08)" : "none", border: "none", borderBottom: `2px solid ${activeTab === id ? "#00ff9d" : "transparent"}`, padding: "12px 0", fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: activeTab === id ? "#00ff9d" : "rgba(255,255,255,0.3)", cursor: "pointer", fontFamily: "monospace" }}>{label}</button>
          ))}
        </div>
      )}

      {/* MAIN CONTENT */}
      {isMobile ? (
        <div style={{ padding: "16px", overflowY: "auto", paddingBottom: 40 }}>
          {activeTab === "account" && <AccountPanel />}
          {activeTab === "position" && <PositionPanel />}
          {activeTab === "inputs" && <InputsPanel />}
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "320px 1fr 300px", height: "calc(100vh - 52px)" }}>
          <div style={{ borderRight: "1px solid rgba(255,255,255,0.06)", padding: "20px 18px", overflowY: "auto" }}><AccountPanel /></div>
          <div style={{ padding: "24px 28px", overflowY: "auto" }}><PositionPanel /></div>
          <div style={{ borderLeft: "1px solid rgba(255,255,255,0.06)", padding: "20px 18px", overflowY: "auto" }}><InputsPanel /></div>
        </div>
      )}
    </div>
  );
}
